import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Logo from './Logo';
import { HomeIcon, Bars3Icon, XMarkIcon, WalletIcon } from '@heroicons/react/24/outline';

function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { currentUser, logout, loading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Add scroll detection for navbar appearance change
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Define navigation items based on user role and current page
  const getNavigationItems = () => {
    const baseNavigation = [
      { name: 'Home', href: '/' },
      { name: 'About', href: '/about' },
      { name: 'Investment Plans', href: '/investment-plans' },
      { name: 'Portfolio', href: '/portfolio' },
      { name: 'Contact', href: '/contact' }
    ];

    // Add dashboard link if user is logged in and not an admin
    if (currentUser && !isAdmin && location.pathname !== '/dashboard') {
      baseNavigation.push({
        name: 'Dashboard',
        href: '/dashboard',
        icon: WalletIcon,
        className: 'text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 rounded-full px-6 py-2 shadow-lg hover:shadow-xl transition-all duration-300'
      });
    }

    return baseNavigation;
  };

  const navigation = getNavigationItems();

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      setIsOpen(false); // Close mobile menu
      await logout();
      // Use replace to prevent going back to protected routes
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      // Continue with navigation even if logout fails
      navigate('/login', { replace: true });
    }
  };

  const menuVariants = {
    closed: {
      opacity: 0,
      scale: 0.95,
      y: -20,
      transition: {
        duration: 0.2,
        ease: 'easeInOut'
      }
    },
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: 'easeOut',
        staggerChildren: 0.05,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    closed: { 
      opacity: 0, 
      y: -10,
      transition: { duration: 0.2 }
    },
    open: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  // Don't render navigation items while auth is loading
  if (loading) {
    return (
      <>
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-6xl px-4">
          <div className="bg-white/20 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl">
            <div className="flex justify-between items-center h-16 px-6">
              <Link to="/" className="flex-shrink-0">
                <Logo />
              </Link>
            </div>
          </div>
        </div>
        <div className="h-24" />
      </>
    );
  }

  return (
    <>
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-6xl px-4">
        <motion.nav 
          className={`relative transition-all duration-500 ${
            scrolled 
              ? 'bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl' 
              : 'bg-white/80 backdrop-blur-lg border border-white/30 shadow-xl'
          } rounded-2xl overflow-hidden`}
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          {/* Animated background gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 via-purple-500/5 to-primary-500/5 opacity-50" />
          
          <div className="relative flex justify-between items-center h-16 px-6">
            <Link to="/" className="flex-shrink-0 z-10">
              <Logo />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex md:items-center md:space-x-2 lg:space-x-6">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <motion.div
                    key={item.href}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link
                      to={item.href}
                      className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-full
                        ${item.className || (isActive 
                          ? 'text-primary-600 bg-primary-50/80 backdrop-blur-sm shadow-md' 
                          : 'text-neutral-700 hover:text-primary-600 hover:bg-white/50 hover:backdrop-blur-sm hover:shadow-md')}`}
                    >
                      {item.icon && <item.icon className="w-4 h-4 mr-2 inline-block" />}
                      {item.name}
                      {isActive && !item.className && (
                        <motion.div 
                          className="absolute inset-0 bg-gradient-to-r from-primary-400/20 to-primary-600/20 rounded-full -z-10"
                          layoutId="navbar-indicator"
                          transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                        />
                      )}
                    </Link>
                  </motion.div>
                );
              })}
              
              <div className="hidden md:flex ml-4 items-center space-x-3">
                {currentUser ? (
                  <>
                    {isAdmin && location.pathname === '/admin' ? (
                      <motion.button
                        onClick={handleLogout}
                        className="inline-flex items-center justify-center px-6 py-2 text-sm font-medium rounded-full text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-lg hover:shadow-xl transition-all duration-300"
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Log Out
                      </motion.button>
                    ) : isAdmin ? (
                      <motion.div
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Link
                          to="/admin"
                          className="inline-flex items-center justify-center px-6 py-2 text-sm font-medium rounded-full text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg hover:shadow-xl transition-all duration-300"
                        >
                          Admin Dashboard
                        </Link>
                      </motion.div>
                    ) : (
                      <motion.button
                        onClick={handleLogout}
                        className="inline-flex items-center justify-center px-6 py-2 text-sm font-medium rounded-full text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-lg hover:shadow-xl transition-all duration-300"
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Log Out
                      </motion.button>
                    )}
                  </>
                ) : (
                  <>
                    <motion.div
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link
                        to="/login"
                        className="inline-flex items-center justify-center px-6 py-2 text-sm font-medium rounded-full text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg hover:shadow-xl transition-all duration-300"
                      >
                        Login
                      </Link>
                    </motion.div>
                    <motion.div
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Link
                        to="/signup"
                        className="inline-flex items-center justify-center px-6 py-2 text-sm font-medium rounded-full text-primary-600 bg-white/80 backdrop-blur-sm hover:bg-white hover:text-primary-700 shadow-md hover:shadow-lg border border-primary-200 hover:border-primary-300 transition-all duration-300"
                      >
                        Create Account
                      </Link>
                    </motion.div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center space-x-3">
              <motion.button
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center justify-center p-2 rounded-full text-neutral-600 hover:text-primary-600 hover:bg-white/50 hover:backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-200"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <span className="sr-only">Open main menu</span>
                <AnimatePresence mode="wait">
                  {!isOpen ? (
                    <motion.div
                      key="hamburger"
                      initial={{ rotate: 0 }}
                      animate={{ rotate: 0 }}
                      exit={{ rotate: 90 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Bars3Icon className="block h-6 w-6" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90 }}
                      animate={{ rotate: 0 }}
                      exit={{ rotate: 90 }}
                      transition={{ duration: 0.2 }}
                    >
                      <XMarkIcon className="block h-6 w-6" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </div>
        </motion.nav>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed top-24 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-md px-4 md:hidden">
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={menuVariants}
              className="bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden"
            >
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-purple-500/5 to-primary-500/5" />
              
              <div className="relative px-6 py-6 space-y-2">
                {navigation.map((item, index) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <motion.div
                      key={item.href}
                      variants={itemVariants}
                      custom={index}
                    >
                      <Link
                        to={item.href}
                        className={`block px-4 py-3 rounded-xl text-base font-medium transition-all duration-300
                          ${isActive 
                            ? 'text-primary-600 bg-primary-50/80 backdrop-blur-sm shadow-md' 
                            : 'text-neutral-700 hover:text-primary-600 hover:bg-white/50 hover:backdrop-blur-sm hover:shadow-md'}`}
                        onClick={() => setIsOpen(false)}
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  );
                })}
                
                <div className="mt-6 space-y-3">
                  {currentUser ? (
                    <>
                      {isAdmin && location.pathname === '/admin' ? (
                        <motion.button
                          onClick={handleLogout}
                          className="block w-full px-4 py-3 text-center text-sm font-medium rounded-xl text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-lg hover:shadow-xl transition-all duration-300"
                          variants={itemVariants}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Log Out
                        </motion.button>
                      ) : isAdmin ? (
                        <motion.div 
                          variants={itemVariants}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Link
                            to="/admin"
                            className="block w-full px-4 py-3 text-center text-sm font-medium rounded-xl text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg hover:shadow-xl transition-all duration-300"
                            onClick={() => setIsOpen(false)}
                          >
                            Admin Dashboard
                          </Link>
                        </motion.div>
                      ) : (
                        <motion.button
                          onClick={handleLogout}
                          className="block w-full px-4 py-3 text-center text-sm font-medium rounded-xl text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 shadow-lg hover:shadow-xl transition-all duration-300"
                          variants={itemVariants}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          Log Out
                        </motion.button>
                      )}
                    </>
                  ) : (
                    <>
                      <motion.div 
                        variants={itemVariants}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Link
                          to="/login"
                          className="block w-full px-4 py-3 text-center text-sm font-medium rounded-xl text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg hover:shadow-xl transition-all duration-300"
                          onClick={() => setIsOpen(false)}
                        >
                          Login
                        </Link>
                      </motion.div>
                      <motion.div 
                        variants={itemVariants}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Link
                          to="/signup"
                          className="block w-full px-4 py-3 text-center text-sm font-medium rounded-xl text-primary-600 bg-white/80 backdrop-blur-sm hover:bg-white hover:text-primary-700 shadow-md hover:shadow-lg border border-primary-200 hover:border-primary-300 transition-all duration-300"
                          onClick={() => setIsOpen(false)}
                        >
                          Create Account
                        </Link>
                      </motion.div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

export default Navigation;